/** Register 주소 저장 — 개인(L_CONTACT_ADDRESS) vs 법인 branch(L_CONTACT_BRANCH) */

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function strField(v: unknown): string {
    return String(v ?? '').trim();
}

export function isRegisterAddressGuidLike(value: unknown): boolean {
    return UUID_RE.test(strField(value));
}

function isBaseCountryCode(value: unknown): boolean {
    return /^B15/i.test(strField(value));
}

function pickDisplayField(...values: unknown[]): string {
    for (const v of values) {
        const s = strField(v);
        if (s && !isRegisterAddressGuidLike(s)) return s;
    }
    return '';
}

/** L_CONTACT_BRANCH — state/city/post_code/country에 location code(GUID·B15) 우선 */
function pickLocationCodeField(...values: unknown[]): string {
    for (const v of values) {
        const s = strField(v);
        if (!s) continue;
        if (isRegisterAddressGuidLike(s) || isBaseCountryCode(s)) return s;
    }
    for (const v of values) {
        const s = strField(v);
        if (s) return s;
    }
    return '';
}

/**
 * L_CONTACT_ADDRESS 저장 — state/city/post_code 표시값
 */
export function normalizeRegisterAddressRowForSave(
    row: Record<string, unknown>,
): Record<string, unknown> {
    const copy = { ...row };
    const stateCode = strField(copy.stateCode ?? copy.STATE_CODE);
    const cityCode = strField(copy.cityCode ?? copy.CITY_CODE);

    let state = pickDisplayField(copy.state, copy.STATE, copy.stateName, copy.STATE_NAME);
    let city = pickDisplayField(copy.city, copy.CITY, copy.cityName, copy.CITY_NAME);
    const postCode = pickDisplayField(
        copy.post_code,
        copy.postcode,
        copy.POST_CODE,
        copy.POSTCODE,
        copy.postalCode,
    );

    if (!state && stateCode && !isRegisterAddressGuidLike(stateCode)) {
        state = stateCode;
    }
    if (!city && cityCode && !isRegisterAddressGuidLike(cityCode)) {
        city = cityCode;
    }

    if (state) {
        copy.state = state;
        copy.STATE = state;
    }
    if (city) {
        copy.city = city;
        copy.CITY = city;
    }
    if (postCode) {
        copy.post_code = postCode;
        copy.postcode = postCode;
        copy.POST_CODE = postCode;
    }
    const postcodeCode = strField(copy.postcodeCode ?? copy.POSTCODE_CODE);
    if (postcodeCode) {
        copy.postcodeCode = postcodeCode;
        copy.POSTCODE_CODE = postcodeCode;
    }
    if (stateCode) {
        copy.stateCode = stateCode;
        copy.STATE_CODE = stateCode;
    }
    if (cityCode) {
        copy.cityCode = cityCode;
        copy.CITY_CODE = cityCode;
    }

    return copy;
}

/**
 * L_CONTACT_BRANCH ADDR_JSON_DATA — Contact branch persist 와 동일 (GUID + B15)
 */
export function normalizeRegisterBranchAddressRowForSave(
    row: Record<string, unknown>,
): Record<string, unknown> {
    const copy = { ...row };
    copy.street01 = strField(copy.street01 ?? copy.addressLine1 ?? copy.STREET01);
    copy.street02 = strField(copy.street02 ?? copy.addressLine2 ?? copy.STREET02);
    copy.street03 = strField(copy.street03 ?? copy.addressLine3 ?? copy.STREET03);
    copy.state = pickLocationCodeField(
        copy.stateCode,
        copy.STATE_CODE,
        copy.state,
        copy.STATE,
    );
    copy.city = pickLocationCodeField(
        copy.cityCode,
        copy.CITY_CODE,
        copy.city,
        copy.CITY,
    );
    copy.post_code = pickLocationCodeField(
        copy.postcodeCode,
        copy.POSTCODE_CODE,
        copy.post_code,
        copy.postcode,
        copy.POST_CODE,
    );
    copy.country = pickLocationCodeField(
        copy.countryCode,
        copy.COUNTRY,
        copy.country,
    );
    if (copy.postcode !== undefined) delete copy.postcode;
    return copy;
}

export function normalizeRegisterAddressListForSave(
    rows: unknown[] | null | undefined,
): Record<string, unknown>[] {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((row) => row && typeof row === 'object')
        .map((row) => normalizeRegisterAddressRowForSave(row as Record<string, unknown>));
}

export function normalizeRegisterBranchAddressListForSave(
    rows: unknown[] | null | undefined,
): Record<string, unknown>[] {
    if (!Array.isArray(rows)) return [];
    return rows
        .filter((row) => row && typeof row === 'object')
        .map((row) => normalizeRegisterBranchAddressRowForSave(row as Record<string, unknown>));
}
